import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * Offers page — visual port of the Remix reference's `routes/offers.index.tsx`.
 *
 * Same two rules the Accounts and Campaigns ports follow:
 *
 * 1. Colour comes from --hd-* tokens only, so both themes work for free.
 * 2. Radii follow the reference's overridden scale (--radius: 0.875rem):
 *      sm 10 · md 12 · lg 14 · xl 18 · 2xl 22 · full 999
 *    Stock Tailwind values (6/8/12/16) make every card visibly boxier.
 *
 * The reference has no search or pagination — this workspace's offer list is
 * server-paginated, so both are kept and styled in the same idiom.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';
const CARD_SHADOW = 'var(--hd-shadow-card)';
const GRADIENT = 'var(--hd-gradient-houdiny)';

/* bg-muted/30 — the reference's empty-state fill. */
const MUTED_30 = 'color-mix(in srgb, var(--hd-muted) 30%, transparent)';
const FG_20 = 'color-mix(in srgb, var(--hd-foreground) 20%, transparent)';
const FG_30 = 'color-mix(in srgb, var(--hd-foreground) 30%, transparent)';
const DESTRUCTIVE_10 = 'color-mix(in srgb, var(--hd-destructive) 10%, transparent)';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const StyledOffers = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};
  /* Same stack the reference sets on html/body via --font-sans. */
  font-family: var(--base-font-sans-serif);
  -webkit-font-smoothing: antialiased;

  /* version3: mx-auto max-w-[1480px] px-8 py-8 */
  .offers-main {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }

  .offers-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;

    @media (max-width: 639px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  /* version3: text-2xl font-semibold tracking-tight. Every size below uses
     Tailwind's paired line height (xs 12/16, sm 14/20, base 16/24, 2xl 24/32). */
  .offers-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    letter-spacing: -0.025em;
    /* GlobalStyles capitalises every h1 — the reference does not. */
    text-transform: none;
    color: ${FG};
  }

  .offers-subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  /* version3: h-10 rounded-xl bg-gradient-houdiny px-4 text-sm font-medium */
  .offers-new-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: #fff;
    background: ${GRADIENT};
    box-shadow: ${CARD_SHADOW};
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.9;
    }

    &:focus-visible {
      outline: 2px solid var(--hd-ring);
      outline-offset: 2px;
    }
  }

  /* Not in the reference — this workspace's list is searchable, so the field
     is kept and styled to match the reference's own .input rule. */
  .offers-search {
    position: relative;
    display: block;
    margin-top: 24px;
    max-width: 360px;

    .search-icon {
      position: absolute;
      top: 50%;
      left: 12px;
      transform: translateY(-50%);
      pointer-events: none;
      color: ${MUTED_FG};
    }

    input {
      width: 100%;
      height: 40px;
      padding: 0 12px 0 36px;
      border: 1px solid ${BORDER};
      border-radius: 14px;
      font-family: inherit;
      font-size: 14px;
      line-height: 20px;
      color: ${FG};
      background: ${BG};
      outline: none;
      transition: border-color 0.12s ease;

      &::placeholder {
        color: ${MUTED_FG};
      }

      &:focus {
        border-color: ${FG_30};
      }
    }
  }

  /* Reference: mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 */
  .offers-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 32px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .offers-empty {
    grid-column: 1 / -1;
    padding: 40px;
    border: 1px dashed ${BORDER};
    border-radius: 22px;
    text-align: center;
    background: ${MUTED_30};

    .empty-icon {
      display: grid;
      place-items: center;
      width: 48px;
      height: 48px;
      margin: 0 auto;
      border-radius: 18px;
      color: ${MUTED_FG};
      background: ${BG};
    }

    .empty-title {
      margin-top: 12px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: ${FG};
    }

    .empty-lead {
      margin: 4px 0 0;
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};
    }

    /* Reference: bg-foreground text-background — an inverted button. */
    .empty-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 36px;
      margin-top: 16px;
      padding: 0 12px;
      border: none;
      border-radius: 14px;
      font-family: inherit;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: ${BG};
      background: ${FG};
      cursor: pointer;
      transition: opacity 0.15s ease;

      &:hover {
        opacity: 0.9;
      }
    }
  }

  .offers-pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;

    .pager-range {
      font-size: 12px;
      color: ${MUTED_FG};
    }

    /* The shared Pagination molecule ships its own list spacing; only the
       alignment inside this footer needs setting. */
    ul {
      margin: 0;
      justify-content: flex-end;
    }
  }
`;

/** Reference: the `<Link>` card in offers.index.tsx. */
export const StyledOfferCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${BG};
  cursor: pointer;
  animation: ${fadeUp} 320ms ease both;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${FG_20};
    box-shadow: ${CARD_SHADOW};
  }

  &:focus-visible {
    outline: 2px solid var(--hd-ring);
    outline-offset: 2px;
  }

  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .card-icon {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 18px;
    color: ${FG};
    background: ${MUTED};
  }

  .card-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .card-delete {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 10px;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      color: var(--hd-destructive);
      background: ${DESTRUCTIVE_10};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .card-arrow {
    flex-shrink: 0;
    color: ${MUTED_FG};
    transition: transform 0.2s ease, color 0.2s ease;
  }

  &:hover .card-arrow {
    transform: translateX(2px);
    color: ${FG};
  }

  .card-name {
    margin-top: 16px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: ${FG};
  }

  /* Reference: line-clamp-2 */
  .card-lead {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  .card-foot {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid ${BORDER};
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .card-sep {
    color: ${BORDER};
  }
`;

/** Status pill in the card footer — the reference has no equivalent. */
export const StyledOfferStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: ${FG};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $tone }) => $tone};
  }
`;
